import React, { useState } from 'react';
import { SimpleProduct } from '@ajat/design-system/ShoppingCart';
import { ComponentPage, DemoCard } from '../../shared';
import { IMG_1, IMG_2, IMG_3 } from './cartDemoData';

export const meta = {
  component: SimpleProduct,
  componentName: 'SimpleProduct',
  title: 'Simple Product',
  group: 'Shopping Cart',
  importPath: '@ajat/design-system/ShoppingCart',
  figmaNode: '3960:555',
  description:
    'A cart line item. Responsive CSS Grid — actions drop to their own row on mobile and sit inline from 1024px. Pass unitPrice as a number and the displayed price recalculates as the quantity changes; pass price as a string and it stays fixed.',
  props: [
    ['image', 'string', '—', 'Product image'],
    ['imageAlt', 'string', "''", 'Alt text'],
    ['title', 'string', '—', 'Product name (typo-h4)'],
    ['price', 'string', '—', 'Fixed price string — ignored when unitPrice is set'],
    ['unitPrice', 'number', '—', 'Price per unit; the row shows unitPrice × quantity + priceSuffix'],
    ['priceState', "'default' | 'discounted' | 'included'", "'default'", 'Price state passed to Price'],
    ['originalPrice', 'string', "''", 'Fixed struck-through price'],
    ['unitOriginalPrice', 'number', '—', 'Struck-through price per unit; scales with quantity'],
    ['options', 'array', '[]', '[{ label, value }] — Color, Size, Navnetryk. A value may be an array of lines.'],
    ['quantity', 'number', '—', 'Controlled quantity'],
    ['onQuantityChange', 'func', '—', 'Fires with the new quantity'],
    ['onEdit', 'func', '—', 'Edit handler (SquarePen). Omit it and the button is hidden.'],
    ['onRemove', 'func', '—', 'Remove handler (X, text-danger). Omit it and the button is hidden.'],
    ['lockQuantity', 'bool', 'false', 'Disables the stepper — used on price-locked paid orders'],
    ['showQuantity', 'bool', 'true', 'false hides the stepper (price = one unit) — one-of lines where every copy is its own line (OVER/LL patches)'],
    ['variant', "'default' | 'highlighted'", "'default'", "'highlighted' = a line's main product: the artwork full width on top in a 240px well, text + actions under it (Figma OVER-LL 3047:44555)"],
    ['onZoom', 'func', '—', 'highlighted only: the zoom button (ZoomIn, tertiary text) in the well\'s top-right corner'],
    ['zoomLabel', 'string', "'Zoom'", 'aria-label of the zoom button'],
    ['imageFit', "'cover' | 'contain'", "'cover'", 'contain = cut-out artwork shown whole (highlighted is always contain)'],
    ['imageBackground', 'string', '—', 'CSS background of the image well (colour / gradient)'],
    ['priceSuffix', 'string', "',-'", "Suffix for computed prices — ',-' for DK, ':-' for SE"],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — keep it in step with the component by hand.',
  usage: [
    {
      title: 'A cart row',
      code: `import { SimpleProduct } from '@ajat/design-system/ShoppingCart';

<SimpleProduct
  image={item.image}
  title={item.name}
  unitPrice={item.unitPrice}          // number → price × quantity
  priceSuffix=",-"
  options={[
    { label: 'Farve', value: item.color },
    { label: 'Størrelse', value: item.size },
    { label: 'Navnetryk', value: item.name },
  ]}
  quantity={item.quantity}
  onQuantityChange={(qty) => updateItem(item.id, qty)}
  onEdit={() => reopenConfigurator(item)}
  onRemove={() => removeItem(item.id)}
/>`,
    },
  ],
};

export default function SimpleProductPage() {
  const [qty1, setQty1] = useState(1);
  const [qty2, setQty2] = useState(2);
  const [qty3, setQty3] = useState(1);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Default" hint="unitPrice × quantity — change the stepper">
        <SimpleProduct
          image={IMG_1}
          imageAlt="Premium Hoodie"
          title="Premium Hoodie"
          unitPrice={449}
          options={[
            { label: 'Color', value: 'Navy' },
            { label: 'Size', value: 'M' },
            { label: 'Navnetryk', value: 'Archie' },
          ]}
          quantity={qty1}
          onQuantityChange={setQty1}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Highlighted" hint={'variant="highlighted" — the line\'s main product: artwork on top, zoom in the corner'}>
        <SimpleProduct
          variant="highlighted"
          image={IMG_1}
          imageAlt="Premium Hoodie"
          imageBackground="var(--color-gray-05)"
          title="Premium Hoodie"
          unitPrice={449}
          options={[
            { label: 'Color', value: 'Navy' },
            { label: 'Size', value: 'M' },
            { label: 'Navnetryk', value: 'Archie' },
          ]}
          quantity={qty1}
          onQuantityChange={setQty1}
          onZoom={() => {}}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Discounted" hint="both prices scale with quantity">
        <SimpleProduct
          image={IMG_2}
          imageAlt="Unisex T-Shirt"
          title="Unisex T-Shirt"
          unitPrice={120}
          priceState="discounted"
          unitOriginalPrice={159}
          options={[
            { label: 'Color', value: 'Red' },
            { label: 'Size', value: 'S' },
          ]}
          quantity={qty2}
          onQuantityChange={setQty2}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Included" hint="free line inside a package">
        <SimpleProduct
          image={IMG_3}
          imageAlt="Beanie"
          title="Beanie"
          price="0,-"
          priceState="included"
          originalPrice="149,-"
          options={[{ label: 'Color', value: 'Black' }]}
          quantity={qty3}
          onQuantityChange={setQty3}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Without action buttons" hint="omit onEdit / onRemove and they disappear">
        <SimpleProduct
          image={IMG_1}
          title="Premium Hoodie"
          unitPrice={449}
          options={[{ label: 'Size', value: 'M' }]}
          quantity={1}
        />
      </DemoCard>

      <DemoCard title="Without options" hint="omit options and the line collapses to title and price">
        <SimpleProduct
          image={IMG_1}
          title="Premium Hoodie"
          unitPrice={449}
          quantity={1}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Without quantity" hint="showQuantity={false} — a one-of line, every copy its own line">
        <SimpleProduct
          image={IMG_3}
          title="Beanie"
          unitPrice={149}
          options={[{ label: 'Color', value: 'Black' }]}
          showQuantity={false}
          onEdit={() => {}}
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="Locked quantity" hint="price-locked paid order">
        <SimpleProduct
          image={IMG_2}
          title="Unisex T-Shirt"
          unitPrice={120}
          options={[{ label: 'Size', value: 'S' }]}
          quantity={2}
          lockQuantity
          onRemove={() => {}}
        />
      </DemoCard>

      <DemoCard title="A full cart">
        <SimpleProduct
          image={IMG_1}
          title="Premium Hoodie"
          unitPrice={449}
          options={[{ label: 'Color', value: 'Navy' }, { label: 'Size', value: 'M' }]}
          quantity={1}
          onEdit={() => {}}
          onRemove={() => {}}
        />
        <SimpleProduct
          image={IMG_2}
          title="Unisex T-Shirt"
          unitPrice={120}
          priceState="discounted"
          unitOriginalPrice={159}
          options={[{ label: 'Color', value: 'Red' }, { label: 'Size', value: 'S' }]}
          quantity={2}
          onEdit={() => {}}
          onRemove={() => {}}
        />
        <SimpleProduct
          image={IMG_3}
          title="Beanie"
          price="0,-"
          priceState="included"
          originalPrice="149,-"
          options={[{ label: 'Color', value: 'Black' }]}
          quantity={1}
          onRemove={() => {}}
        />
      </DemoCard>
    </ComponentPage>
  );
}
